import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import {connect} from 'react-redux';
import ReactTooltip from 'react-tooltip';

import {reverse} from 'utils/urls';
import {formatDateForApi, formatDate, DATE_FORMAT_API} from 'utils/datetime';
import {BOOKING_SET_DETAIL_URL} from 'config/urls';

import Table from 'components/Table';
import Button from 'components/Button';
import Tooltip from 'components/Tooltip';
import BooleanIcon from 'components/BooleanIcon';
import Loading from 'components/Loading';
import RenewalIcon from 'components/RenewalIcon';
import ToggleSwitch from 'components/ToggleSwitch';
import {withSorting, SORTING, WithSortingProps} from 'components/withSorting';
import NotesCount from 'components/NotesCount';

import ClickIconWithNoPropagation from 'components/ClickIconWithNoPropagation';

import {
  EmptyListPlaceholder,
  TableSplitter,
  Owed,
  TenantCell,
  BookingType,
  BookingSetBalance
} from 'containers/FrontDesk/components/shared';

import {CompleteRoomMoveModal, RenewBookingModal} from './components';

import {
  fetchRoomMoves,
  fetchCompletedRoomMoves,
  selectRoomMoves,
  selectRoomMovesLoading,
  selectCompletedRoomMoves,
  selectCompletedRoomMovesLoading,
  completeRoomMove,
  undoCompleteRoomMove,
  fetchPendingRenewals,
  selectPendingRenewals,
  selectPendingRenewalsLoading,
  renewBooking,
  undoRenewBooking,
  fetchCompletedRenewals,
  selectCompletedRenewals,
  selectCompletedRenewalsLoading,
  MODALS
} from 'ducks/frontDesk';
import {
  requiresPermission,
  PERMISSIONS,
  requiresMultiplePermissions
} from 'ducks/auth';
import {
  isModalOpen,
  openModal,
  closeModal,
  MODALS as BASE_MODALS
} from 'ducks/modals';
import {exportCsv, selectExporting, exportCsvInBackground} from 'ducks/files';
import {ConfirmationModal} from 'components/CommonModals';

import {
  exportRoomMovesAndRenewalsCsv as exportRoomMovesAndRenewalsCsvSdk,
  exportRoomMovesAndRenewalsCsvInBackground as exportRoomMovesAndRenewalsCsvInBackgroundSdk
} from 'sdk/frontDesk';

import './styles.css';

const ROOM_MOVES_AND_RENEWALS_CSV_FILENAME = 'room_moves_and_stayovers';
const TOOLTIP_NAME = 'roomMovesAndStayoversTableTooltip';

const RenewalRow = ({
  primaryTenant,
  oldBooking,
  newBooking,
  onRenewClick,
  openRoomInspectionModal,
  isToday,
  completed,
  openBookingNotesModal,
  renewalDate,
  departureDate,
  redirectToBookingSet,
  hasRoomMove,
  undoRenewBooking
}) => {
  const currentAllocation = newBooking.currentRoomAllocation;

  return (
    <Table.Row
      onClick={() =>
        redirectToBookingSet({bookingSetId: newBooking.bookingSetId})
      }>
      <Table.Cell className="name">
        <div className="colab-centered-v">
          <TenantCell primaryTenant={primaryTenant} />
          <RenewalIcon />
        </div>
      </Table.Cell>
      <Table.Cell className="type">
        <div
          data-tip={JSON.stringify({
            type: 'bookingType',
            content: oldBooking.type
          })}
          data-for={TOOLTIP_NAME}>
          <BookingType bookingType={oldBooking.type} />
        </div>
      </Table.Cell>
      <Table.Cell className="from">
        {_.get(oldBooking, 'currentRoomAllocation.roomNumber')}
      </Table.Cell>
      <Table.Cell className="to">
        {_.get(currentAllocation, 'roomNumber')}
      </Table.Cell>
      <Table.Cell className="startDate">{formatDate(renewalDate)}</Table.Cell>
      <Table.Cell className="endDate">{formatDate(departureDate)}</Table.Cell>
      <Table.Cell className="balance">
        <div
          data-tip={JSON.stringify({
            bookingSetRentAndOneOffBalance:
              newBooking.bookingSetRentAndOneOffBalance,
            bookingSetDepositBalance: newBooking.bookingSetDepositBalance
          })}
          data-for={TOOLTIP_NAME}>
          <Owed amount={newBooking.bookingSetBalance} />
        </div>
      </Table.Cell>
      <Table.Cell className="notesCount">
        <NotesCount
          notesCount={oldBooking.notesCount}
          showNotification={oldBooking.hasUnresolvedNotes}
          onClick={() => openBookingNotesModal({bookingId: oldBooking.id})}
        />
      </Table.Cell>
      <Table.Cell className="newRoomInspected">
        <ClickIconWithNoPropagation
          onClick={() =>
            openRoomInspectionModal({
              inspectionPassed: _.get(
                currentAllocation,
                'roomInspectionPassed'
              ),
              allocationId: _.get(currentAllocation, 'id'),
              bookingId: newBooking.id
            })
          }>
          <BooleanIcon
            value={_.get(currentAllocation, 'roomInspectionPassed')}
          />
        </ClickIconWithNoPropagation>
      </Table.Cell>
      <Table.Cell className="agreementSigned">
        <BooleanIcon value={newBooking.agreementSigned} />
      </Table.Cell>

      <Table.Cell className="actionButton">
        {!completed && (
          <ClickIconWithNoPropagation
            onClick={() =>
              onRenewClick({
                oldBooking: oldBooking.id,
                newBooking: newBooking.id,
                hasRoomMove: hasRoomMove
              })
            }>
            <Button white disabled={!isToday}>
              {hasRoomMove && 'Renew & Change Room'}
              {!hasRoomMove && 'Renew'}
            </Button>
          </ClickIconWithNoPropagation>
        )}
        {completed && (
          <ClickIconWithNoPropagation
            onClick={() => undoRenewBooking(oldBooking, newBooking)}>
            <Button white>Undo renewal</Button>
          </ClickIconWithNoPropagation>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

const RoomMoveRow = ({
  id,
  booking,
  completed,
  oldAllocation,
  newAllocation,
  onChangeRoomClick,
  openRoomInspectionModal,
  isToday,
  openBookingNotesModal,
  notesCount,
  hasUnresolvedNotes,
  roomMoveDate,
  departureDate,
  redirectToBookingSet,
  undoCompleteRoomMove
}) => {
  return (
    <Table.Row
      onClick={() =>
        redirectToBookingSet({bookingSetId: booking.bookingSetId})
      }>
      <Table.Cell className="name">
        <TenantCell primaryTenant={booking.primaryTenant} />
      </Table.Cell>
      <Table.Cell className="type">
        <div
          data-tip={JSON.stringify({
            type: 'bookingType',
            content: booking.type
          })}
          data-for={TOOLTIP_NAME}>
          <BookingType bookingType={booking.type} />
        </div>
      </Table.Cell>
      <Table.Cell className="from">{oldAllocation.room.roomNumber}</Table.Cell>
      <Table.Cell className="to">{newAllocation.room.roomNumber}</Table.Cell>
      <Table.Cell className="startDate">{formatDate(roomMoveDate)}</Table.Cell>
      <Table.Cell className="endDate">{formatDate(departureDate)}</Table.Cell>
      <Table.Cell className="balance">
        <div
          data-tip={JSON.stringify({
            bookingSetRentAndOneOffBalance:
              booking.bookingSetRentAndOneOffBalance,
            bookingSetDepositBalance: booking.bookingSetDepositBalance
          })}
          data-for={TOOLTIP_NAME}>
          <Owed amount={booking.bookingSetBalance} />
        </div>
      </Table.Cell>
      <Table.Cell className="notesCount">
        <NotesCount
          notesCount={notesCount}
          showNotification={hasUnresolvedNotes}
          onClick={() => openBookingNotesModal({bookingId: booking.id})}
        />
      </Table.Cell>
      <Table.Cell className="newRoomInspected">
        <ClickIconWithNoPropagation
          onClick={() =>
            openRoomInspectionModal({
              inspectionPassed: newAllocation.roomInspectionPassed,
              allocationId: newAllocation.id,
              bookingId: booking.id
            })
          }>
          <BooleanIcon value={newAllocation.roomInspectionPassed} />
        </ClickIconWithNoPropagation>
      </Table.Cell>
      <Table.Cell className="agreementSigned">
        <BooleanIcon value={booking.agreementSigned} />
      </Table.Cell>
      <Table.Cell className="actionButton">
        {!completed && (
          <ClickIconWithNoPropagation onClick={() => onChangeRoomClick(id)}>
            <Button white disabled={!isToday}>
              Change Room
            </Button>
          </ClickIconWithNoPropagation>
        )}
        {completed && (
          <ClickIconWithNoPropagation onClick={() => undoCompleteRoomMove(id)}>
            <Button white>Undo change room</Button>
          </ClickIconWithNoPropagation>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

const ROOM_CHANGES = 'Room Changes';
const STAYOVERS = 'Stayovers';

const TOGGLE_SECTIONS = [ROOM_CHANGES, STAYOVERS];

const Switcher = ({toggledSection, toggleSection}) => (
  <div className="Switcher colab-row-space-between colab-sidepadding-1">
    {TOGGLE_SECTIONS.map(section => (
      <div
        key={section}
        className="Switcher__section colab-row-space-between colab-centered-v">
        <div className="Switcher__label colab-sidepadding-1">{section}</div>
        <ToggleSwitch
          value={_.includes(toggledSection, section)}
          onChange={() => toggleSection(section)}
        />
      </div>
    ))}
  </div>
);

interface RoomMovesAndStayoversTableProps {
  filter: {[key: string]: any};
  locationRef: string;
}

class RoomMovesAndStayoversTable extends React.Component<
  RoomMovesAndStayoversTableProps &
    WithSortingProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  state = {
    isOpened: false,
    toggledSection: [STAYOVERS, ROOM_CHANGES]
  };

  toggleSection = selectedSection => {
    this.setState({
      toggledSection: _.xor(this.state.toggledSection, [selectedSection])
    });
  };

  getOption = () => this.props.filter.value;

  isToday = () => this.props.filter.value === 'today';

  fetchRoomMoves = () => {
    const {locationRef} = this.props;

    this.props.fetchRoomMoves({
      locationRef,
      option: this.getOption(),
      dateFrom: formatDateForApi(this.props.filter.dateFrom),
      dateTo: formatDateForApi(this.props.filter.dateTo)
    });
  };

  fetchCompletedRoomMoves = () =>
    this.props.fetchCompletedRoomMoves({
      locationRef: this.props.locationRef,
      option: this.getOption()
    });

  fetchPendingRenewals = () => {
    const {locationRef} = this.props;

    this.props.fetchPendingRenewals({
      locationRef,
      option: this.getOption(),
      dateFrom: formatDateForApi(this.props.filter.dateFrom),
      dateTo: formatDateForApi(this.props.filter.dateTo)
    });
  };

  fetchCompletedRenewals = () => {
    const {locationRef} = this.props;
    this.props.fetchCompletedRenewals({locationRef, option: this.getOption()});
  };

  openCompleteRoomChangeModal = roomMoveId => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.COMPLETE_ROOM_MOVE, locationRef},
      () =>
        this.props.openModal(MODALS.COMPLETE_ROOM_MOVE, {
          roomMoveId,
          locationRef
        })
    );
  };

  fetchRoomMovesExtraAction = () => {
    const {locationRef} = this.props;
    const option = this.getOption();

    return [
      () =>
        fetchRoomMoves({
          locationRef,
          option,
          dateFrom: formatDateForApi(this.props.filter.dateFrom),
          dateTo: formatDateForApi(this.props.filter.dateTo)
        }),
      () => fetchCompletedRoomMoves({locationRef, option})
    ];
  };

  openRenewBookingModal = ({oldBooking, newBooking, hasRoomMove}) => {
    const {locationRef} = this.props;
    this.props.requiresMultiplePermissions(
      {
        permissions: [
          {permission: PERMISSIONS.CREATE_CHECK_IN, locationRef},
          {permission: PERMISSIONS.CREATE_CHECK_OUT, locationRef}
        ],
        disableClose: false
      },
      () =>
        this.props.openModal(MODALS.RENEW_BOOKING, {
          newBooking,
          oldBooking,
          hasRoomMove,
          locationRef
        })
    );
  };

  showCompletedRoomMovesAndRenewalsToday = () => {
    this.setState({isOpened: true}, () => {
      this.fetchCompletedRoomMoves();
      this.fetchCompletedRenewals();
    });
  };

  hideCompletedRoomMovesAndRenewalsToday = () => {
    this.setState({isOpened: false});
  };

  exportRoomMovesAndRenewalsCsv = () => {
    this.props.exportCsv({
      exportData: {
        locationRef: this.props.locationRef,
        option: this.getOption(),
        dateFrom: formatDateForApi(this.props.filter.dateFrom),
        dateTo: formatDateForApi(this.props.filter.dateTo),
        exportRoomMoves: _.includes(this.state.toggledSection, ROOM_CHANGES),
        exportRenewals: _.includes(this.state.toggledSection, STAYOVERS)
      },
      exportSdk: exportRoomMovesAndRenewalsCsvSdk,
      fileName: ROOM_MOVES_AND_RENEWALS_CSV_FILENAME
    });
  };

  exportRoomMovesAndRenewalsCsvInBackground = () => {
    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_CSV_EXPORT_FILE
      },
      () =>
        this.props.exportCsvInBackground({
          exportData: {
            locationRef: this.props.locationRef,
            option: this.getOption(),
            dateFrom: formatDateForApi(this.props.filter.dateFrom),
            dateTo: formatDateForApi(this.props.filter.dateTo),
            exportRoomMoves: _.includes(
              this.state.toggledSection,
              ROOM_CHANGES
            ),
            exportRenewals: _.includes(this.state.toggledSection, STAYOVERS)
          },
          exportSdk: exportRoomMovesAndRenewalsCsvInBackgroundSdk,
          fileName: ROOM_MOVES_AND_RENEWALS_CSV_FILENAME
        })
    );
  };

  openBookingNotesModal = ({bookingId, fetchCompleted}) => {
    const {locationRef} = this.props;

    const refetchExtraAction = () =>
      fetchCompleted
        ? this.fetchCompletedSectionExtraActions()
        : this.fetchPendingSectionExtraActions();

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_BOOKING_NOTES,
        locationRef
      },
      () => {
        this.props.openModal(BASE_MODALS.BOOKING_NOTES, {
          bookingId,
          locationRef,
          refetchExtraAction
        });
      }
    );
  };

  componentDidMount() {
    this.fetchRoomMoves();
    this.fetchPendingRenewals();
  }

  componentDidUpdate(prevProps) {
    if (this.props.filter.value !== prevProps.filter.value && !this.isToday()) {
      this.setState({isOpened: false});
    }

    if (
      this.props.filter.value !== prevProps.filter.value ||
      this.props.filter.dateFrom !== prevProps.filter.dateFrom ||
      this.props.filter.dateTo !== prevProps.filter.dateTo
    ) {
      this.fetchRoomMoves();
      this.fetchPendingRenewals();
    }

    const roomMovesAreFetched = !_.isEqual(
      prevProps.roomMoves,
      this.props.roomMoves
    );
    const roomMovesDoneTodayAreFetched = !_.isEqual(
      prevProps.roomMovesDoneToday,
      this.props.roomMovesDoneToday
    );
    const pendingRenewalsAreFetched = !_.isEqual(
      prevProps.pendingRenewals,
      this.props.pendingRenewals
    );
    const completedRenewalsAreFetched = !_.isEqual(
      prevProps.completedRenewals,
      this.props.completedRenewals
    );
    if (
      roomMovesAreFetched ||
      roomMovesDoneTodayAreFetched ||
      pendingRenewalsAreFetched ||
      completedRenewalsAreFetched
    ) {
      ReactTooltip.rebuild();
    }
  }

  getSortedTableData = data => {
    const sort = this.props.getSort();

    if (sort.field === 'sortDate' && sort.direction === '+') {
      return _.sortBy(data, [
        el => moment(el.sortDate, DATE_FORMAT_API).unix()
      ]);
    }

    if (sort.field === 'sortDate' && sort.direction === '-') {
      return _.sortBy(data, [
        el => -moment(el.sortDate, DATE_FORMAT_API).unix()
      ]);
    }

    return data;
  };

  redirectToBookingSet = ({bookingSetId}) => {
    const {locationRef} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        window.open(
          reverse(BOOKING_SET_DETAIL_URL, {locationRef, bookingSetId})
        );
      }
    );
  };

  openRoomInspectionPassModal = ({allocationId, bookingId, fetchCompleted}) => {
    const {locationRef} = this.props;

    const confirmExtraActions = fetchCompleted
      ? this.fetchCompletedSectionExtraActions()
      : this.fetchPendingSectionExtraActions();

    this.props.requiresPermission(
      {permission: PERMISSIONS.EDIT_ROOM_INSPECTION, locationRef},
      () =>
        this.props.openModal(MODALS.ROOM_INSPECTION_PASS, {
          bookingId,
          allocationId,
          locationRef: this.props.locationRef,
          confirmExtraActions
        })
    );
  };

  fetchCompletedSectionExtraActions = () => {
    const {locationRef} = this.props;
    const option = this.getOption();

    return [
      () => fetchCompletedRoomMoves({locationRef, option}),
      () => fetchCompletedRenewals({locationRef, option})
    ];
  };

  fetchPendingSectionExtraActions = () => {
    const {locationRef} = this.props;
    const option = this.getOption();

    return [
      () =>
        fetchRoomMoves({
          locationRef,
          option,
          dateFrom: formatDateForApi(this.props.filter.dateFrom),
          dateTo: formatDateForApi(this.props.filter.dateTo)
        }),
      () =>
        fetchPendingRenewals({
          locationRef,
          option,
          dateFrom: formatDateForApi(this.props.filter.dateFrom),
          dateTo: formatDateForApi(this.props.filter.dateTo)
        })
    ];
  };

  openRoomInspectionRemoveModal = ({
    allocationId,
    bookingId,
    fetchCompleted
  }) => {
    const {locationRef} = this.props;

    // If inspection is passed for the completed renewals/room changes section, we want refetch only
    // the completed renewals/room changes. The same goes for the non completed section.
    const confirmExtraActions = fetchCompleted
      ? this.fetchCompletedSectionExtraActions()
      : this.fetchPendingSectionExtraActions();

    this.props.requiresPermission(
      {permission: PERMISSIONS.EDIT_ROOM_INSPECTION, locationRef},
      () =>
        this.props.openModal(MODALS.ROOM_INSPECTION_REMOVE, {
          bookingId,
          allocationId,
          locationRef: this.props.locationRef,
          confirmExtraActions
        })
    );
  };

  openRoomInspectionModal = ({
    allocationId,
    bookingId,
    inspectionPassed,
    fetchCompleted
  }) => {
    inspectionPassed
      ? this.openRoomInspectionRemoveModal({
          allocationId,
          bookingId,
          fetchCompleted
        })
      : this.openRoomInspectionPassModal({
          allocationId,
          bookingId,
          fetchCompleted
        });
  };

  undoRenewBooking = (oldBooking, newBooking) => {
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Undo complete renewal',
      question: 'Are you sure you want to undo this renewal?',
      confirmAction: () => {
        const {locationRef} = this.props;

        this.props.undoRenewBooking(
          {
            locationRef: locationRef,
            oldBooking: oldBooking.id,
            newBooking: newBooking.id
          },
          [
            ...this.fetchCompletedSectionExtraActions(),
            ...this.fetchPendingSectionExtraActions(),
            () => closeModal(BASE_MODALS.CONFIRM)
          ]
        );
      }
    });
  };

  undoCompleteRoomMove = id => {
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Undo room move completion',
      question: 'Are you sure you want to undo this room move?',
      confirmAction: () => {
        const {locationRef} = this.props;
        this.props.undoCompleteRoomMove(
          {
            locationRef: locationRef,
            roomMoveId: id
          },
          [
            ...this.fetchCompletedSectionExtraActions(),
            ...this.fetchPendingSectionExtraActions(),
            () => closeModal(BASE_MODALS.CONFIRM)
          ]
        );
      }
    });
  };

  fetchTooltipData = data =>
    !_.isEmpty(data) && (
      <BookingSetBalance
        bookingSetRentAndOneOffBalance={data.bookingSetRentAndOneOffBalance}
        bookingSetDepositBalance={data.bookingSetDepositBalance}
      />
    );

  render() {
    const {isOpened} = this.state;
    const {
      roomMovesLoading,
      roomMovesDoneTodayLoading,
      pendingRenewalsLoading,
      completedRenewalsLoading,
      exportingCsv,
      modals: {completeRoomMoveModal, confirmationModal, renewBookingModal}
    } = this.props;

    const renewalsSelected = _.includes(this.state.toggledSection, STAYOVERS);
    const roomMovesSelected = _.includes(
      this.state.toggledSection,
      ROOM_CHANGES
    );

    let pendingData: any = [];
    if (roomMovesSelected) {
      pendingData = [
        ...pendingData,
        ...this.props.roomMoves.map(roomMove => ({
          ...roomMove,
          type: 'roomMove',
          sortDate: roomMove.moveRoomDate
        }))
      ];
    }
    if (renewalsSelected) {
      pendingData = [
        ...pendingData,
        ...this.props.pendingRenewals.map(renewal => ({
          ...renewal,
          type: 'renewal',
          sortDate: renewal.renewalDate
        }))
      ];
    }

    const sortedPendingData = this.getSortedTableData(pendingData);

    let completedData: any = [];
    if (roomMovesSelected) {
      completedData = [
        ...completedData,
        ...this.props.roomMovesDoneToday.map(roomMove => ({
          ...roomMove,
          type: 'roomMove',
          sortDate: roomMove.moveRoomDate
        }))
      ];
    }

    if (renewalsSelected) {
      completedData = [
        ...completedData,
        ...this.props.completedRenewals.map(renewal => ({
          ...renewal,
          type: 'renewal',
          sortDate: renewal.renewalDate
        }))
      ];
    }

    const sortedCompletedData = this.getSortedTableData(completedData);

    const tableLoading = roomMovesLoading || pendingRenewalsLoading;
    const tableLoadedWithNoData = !tableLoading && _.isEmpty(sortedPendingData);

    const completedTableLoading =
      roomMovesDoneTodayLoading || completedRenewalsLoading;
    const completedTableLoadedWithNoData =
      !completedTableLoading && _.isEmpty(sortedCompletedData);

    return (
      <div className="RoomMovesAndStayoversTable">
        <div className="heading colab-sidepadding-2 colab-centered-v">
          <div className="label colab-bold">Room Changes & Stayovers</div>
        </div>
        <Switcher
          toggleSection={this.toggleSection}
          toggledSection={this.state.toggledSection}
        />
        <Table>
          <Table.Header>
            <Table.HeaderItem heading="name" className="name" />
            <Table.HeaderItem heading="type" className="type" />
            <Table.HeaderItem heading="from" className="from" />
            <Table.HeaderItem heading="to" className="to" />
            <Table.HeaderItem
              heading="start date"
              className="startDate"
              sortDirection={
                this.props.getSort().field == 'sortDate' &&
                this.props.getSort().direction
              }
              onSort={() => this.props.onSort('sortDate')}
            />
            <Table.HeaderItem heading="end date" className="endDate" />
            <Table.HeaderItem heading="owed" className="balance" />
            <Table.HeaderItem heading="notes" className="notesCount" />
            <Table.HeaderItem
              heading="New Room Inspected"
              className="newRoomInspected"
            />
            <Table.HeaderItem heading="agreement" className="agreementSigned" />
            <Table.HeaderItem className="actionButton" />
          </Table.Header>
        </Table>
        <div className="pendingSection">
          {tableLoading && <Loading />}
          {tableLoadedWithNoData && <EmptyListPlaceholder />}
          {!tableLoading && (
            <Table>
              <Table.Body>
                {sortedPendingData.map(element => {
                  if (element.type == 'roomMove') {
                    return (
                      <RoomMoveRow
                        key={element.id.toString()}
                        {...element}
                        onChangeRoomClick={this.openCompleteRoomChangeModal}
                        openRoomInspectionModal={this.openRoomInspectionModal}
                        isToday={this.isToday()}
                        notesCount={element.notesCount}
                        openBookingNotesModal={this.openBookingNotesModal}
                        roomMoveDate={element.moveRoomDate}
                        redirectToBookingSet={this.redirectToBookingSet}
                      />
                    );
                  }
                  return (
                    <RenewalRow
                      key={`old ${element.oldBooking.id} - new ${
                        element.newBooking.id
                      }`}
                      {...element}
                      onRenewClick={this.openRenewBookingModal}
                      openRoomInspectionModal={this.openRoomInspectionModal}
                      isToday={this.isToday()}
                      openBookingNotesModal={this.openBookingNotesModal}
                      redirectToBookingSet={this.redirectToBookingSet}
                    />
                  );
                })}
              </Table.Body>
            </Table>
          )}
        </div>

        <TableSplitter
          isOpened={isOpened}
          showSplitButton={this.isToday()}
          splitButtonText="Completed"
          triggerShow={this.showCompletedRoomMovesAndRenewalsToday}
          triggerHide={this.hideCompletedRoomMovesAndRenewalsToday}
          exportDisabled={
            tableLoading || exportingCsv || _.isEmpty(sortedPendingData)
          }
          exportButtonTitle="Export selected"
          triggerExport={this.exportRoomMovesAndRenewalsCsv}
          triggerExperimentalExport={
            this.exportRoomMovesAndRenewalsCsvInBackground
          }
        />

        {isOpened && (
          <div className="completedSection">
            {completedTableLoading && <Loading />}
            {completedTableLoadedWithNoData && <EmptyListPlaceholder />}
            {!completedTableLoading && (
              <Table>
                <Table.Body>
                  {sortedCompletedData.map(element => {
                    if (element.type == 'roomMove') {
                      return (
                        <RoomMoveRow
                          key={element.id.toString()}
                          {...element}
                          onChangeRoomClick={this.openCompleteRoomChangeModal}
                          openRoomInspectionModal={({
                            allocationId,
                            bookingId,
                            inspectionPassed
                          }) =>
                            this.openRoomInspectionModal({
                              allocationId,
                              bookingId,
                              inspectionPassed,
                              fetchCompleted: true
                            })
                          }
                          isToday={this.isToday()}
                          notesCount={element.notesCount}
                          openBookingNotesModal={({bookingId}) =>
                            this.openBookingNotesModal({
                              bookingId,
                              fetchCompleted: true
                            })
                          }
                          roomMoveDate={element.moveRoomDate}
                          redirectToBookingSet={this.redirectToBookingSet}
                          undoCompleteRoomMove={this.undoCompleteRoomMove}
                        />
                      );
                    }
                    return (
                      <RenewalRow
                        key={`old ${element.oldBooking.id} - new ${
                          element.newBooking.id
                        }`}
                        {...element}
                        onRenewClick={this.openRenewBookingModal}
                        openRoomInspectionModal={({
                          allocationId,
                          bookingId,
                          inspectionPassed
                        }) =>
                          this.openRoomInspectionModal({
                            allocationId,
                            bookingId,
                            inspectionPassed,
                            fetchCompleted: true
                          })
                        }
                        isToday={this.isToday()}
                        completed={true}
                        openBookingNotesModal={({bookingId}) =>
                          this.openBookingNotesModal({
                            bookingId,
                            fetchCompleted: true
                          })
                        }
                        redirectToBookingSet={this.redirectToBookingSet}
                        undoRenewBooking={this.undoRenewBooking}
                      />
                    );
                  })}
                </Table.Body>
              </Table>
            )}
          </div>
        )}
        {completeRoomMoveModal && (
          <CompleteRoomMoveModal
            fetchRoomMovesExtraAction={this.fetchRoomMovesExtraAction}
          />
        )}
        {confirmationModal && <ConfirmationModal />}
        {renewBookingModal && (
          <RenewBookingModal
            fetchCompletedSectionExtraActions={
              this.fetchCompletedSectionExtraActions
            }
            fetchPendingSectionExtraActions={
              this.fetchPendingSectionExtraActions
            }
          />
        )}
        <Tooltip
          place="left"
          effect="solid"
          id={TOOLTIP_NAME}
          getContent={tooltipData => {
            const data = tooltipData && JSON.parse(tooltipData);

            const content =
              data && data.type === 'bookingType'
                ? data.content
                : this.fetchTooltipData(data);
            return content;
          }}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  exportingCsv: selectExporting(state, ROOM_MOVES_AND_RENEWALS_CSV_FILENAME),
  roomMoves: selectRoomMoves(state),
  roomMovesLoading: selectRoomMovesLoading(state),
  roomMovesDoneToday: selectCompletedRoomMoves(state),
  roomMovesDoneTodayLoading: selectCompletedRoomMovesLoading(state),
  pendingRenewals: selectPendingRenewals(state),
  pendingRenewalsLoading: selectPendingRenewalsLoading(state),
  completedRenewals: selectCompletedRenewals(state),
  completedRenewalsLoading: selectCompletedRenewalsLoading(state),
  modals: {
    completeRoomMoveModal: isModalOpen(state, MODALS.COMPLETE_ROOM_MOVE),
    confirmationModal: isModalOpen(state, BASE_MODALS.CONFIRM),
    renewBookingModal: isModalOpen(state, MODALS.RENEW_BOOKING)
  }
});

const mapDispatchToProps = {
  openModal,
  fetchRoomMoves,
  fetchCompletedRoomMoves,
  completeRoomMove,
  undoCompleteRoomMove,
  requiresPermission,
  exportCsv,
  exportCsvInBackground,
  fetchPendingRenewals,
  renewBooking,
  undoRenewBooking,
  fetchCompletedRenewals,
  requiresMultiplePermissions
};

export default withSorting(
  connect(mapStateToProps, mapDispatchToProps)(RoomMovesAndStayoversTable),
  SORTING.ROOM_MOVES_AND_STAYOVERS_TABLE
) as React.ComponentType<RoomMovesAndStayoversTableProps>;
